import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { Tag } from 'antd';
import { FC, useCallback, useMemo, useState } from 'react';

import { formatDate } from 'src/utils/date-util';

import AgCellChannel from 'src/atomic-components/atoms/AgCell/AgCellChannel';
import DownloadRawDataCell from 'src/atomic-components/atoms/AgCell/AgCellDownloadRawData';

import { MessageRowData } from './MessageHistory';

interface MessageTableProps {
  rowData: MessageRowData[];
  onSelectMessage: (messageId: string | null) => void;
}

const sourceConfig: Record<string, { label: string; color: string }> = {
  trigger: { label: '푸시 발송', color: 'blue' },
  noTrigger: { label: '일반', color: 'default' },
};

const messageTypeConfig: Record<string, { label: string; color: string }> = {
  text: { label: '텍스트', color: 'default' },
  image: { label: '이미지', color: 'cyan' },
  link: { label: '링크', color: 'purple' },
};

const senderCollectionConfig: Record<string, { label: string; color: string }> = {
  user: { label: '유저', color: 'green' },
  store: { label: '매장', color: 'orange' },
  system: { label: '시스템', color: 'geekblue' },
};

const SourceCellRenderer: FC<ICellRendererParams<MessageRowData>> = (params) => {
  if (!params.value) return null;
  const config = sourceConfig[params.value] ?? { label: params.value, color: 'default' };
  return <Tag color={config.color}>{config.label}</Tag>;
};

const TypeCellRenderer: FC<ICellRendererParams<MessageRowData>> = (params) => {
  if (!params.value) return null;
  const config = messageTypeConfig[params.value] ?? { label: params.value, color: 'default' };
  return <Tag color={config.color}>{config.label}</Tag>;
};

const SenderCollectionCellRenderer: FC<ICellRendererParams<MessageRowData>> = (params) => {
  if (!params.value) return null;
  const config = senderCollectionConfig[params.value] ?? { label: params.value, color: 'default' };
  return <Tag color={config.color}>{config.label}</Tag>;
};

const defaultColDef: ColDef<MessageRowData> = {
  sortable: true,
  resizable: true,
  filter: true,
};

const MessageTable: FC<MessageTableProps> = ({ rowData, onSelectMessage }) => {
  const [gridRef, setGridRef] = useState<AgGridReact<MessageRowData> | null>(null);

  const columnDefs: ColDef<MessageRowData>[] = useMemo(
    () => [
      {
        field: 'createdAt',
        headerName: '생성일',
        width: 170,
        valueFormatter: (params) => (params.value ? formatDate(new Date(params.value), 'yyyy-MM-dd HH:mm') : ''),
      },
      {
        field: 'channelName',
        headerName: '채널',
        width: 180,
        cellRenderer: AgCellChannel,
        cellRendererParams: (params: ICellRendererParams<MessageRowData>) => ({
          channelId: params.data?.channelId,
          field: 'name',
        }),
      },
      { field: 'senderService', headerName: '발송 서비스', width: 120 },
      { field: 'senderCollection', headerName: '발송자 유형', width: 120, cellRenderer: SenderCollectionCellRenderer },
      { field: 'title', headerName: '제목', minWidth: 160, flex: 1 },
      { field: 'body', headerName: '내용', minWidth: 200, flex: 2 },
      { field: 'type', headerName: '메시지 유형', width: 120, cellRenderer: TypeCellRenderer },
      { field: 'category', headerName: '카테고리', width: 120 },
      {
        field: 'source',
        headerName: '구분',
        width: 120,
        cellRenderer: SourceCellRenderer,
      },
      {
        headerName: '액션',
        width: 120,
        cellRenderer: DownloadRawDataCell,
      },
    ],
    [],
  );

  const autoSizeAll = useCallback(() => {
    gridRef?.columnApi?.autoSizeAllColumns(true);
  }, [gridRef]);

  const handleRowClicked = useCallback(
    (event: { data?: MessageRowData }) => {
      const messageId = event.data?.source === 'trigger' ? event.data._id : null;
      onSelectMessage(messageId);
    },
    [onSelectMessage],
  );

  return (
    <div className='ag-theme-alpine' style={{ width: '100%', height: '100%' }}>
      <AgGridReact
        ref={setGridRef}
        rowData={rowData}
        onGridReady={autoSizeAll}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        suppressScrollOnNewData={true}
        alwaysShowHorizontalScroll={true}
        onRowClicked={handleRowClicked}
        rowSelection='single'
        rowHeight={42}
        headerHeight={40}
        suppressCellFocus={true}
      />
    </div>
  );
};

export default MessageTable;
